import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { message } from "antd";
import { BuildQuotesStates } from "../../enums/BuildQuoteStates";
import {
  notificationConstants,
  ErrorTypes,
  ValidationMessages,
  DateFormat,
} from "../../Components/CreateJob/CreateJobView/BuildQuote/constants";
import { fieldNames } from "../../Components/CreateJob/CreateJobView/BuildQuote/constant";
import { regexPatterns } from "../../utils/regexUtil";
import moment from "moment";

const {
  emailTestRegex,
  alphaNumericRegex,
  alphabetsOnlyRegexWithSpace,
  phoneNumberRegex,
} = regexPatterns;

export const useQuoteValidation = () => {
  const { getValues, setError, clearErrors } = useFormContext();

  const validateDownloadAndSend = useCallback(
    (
      showEstimate: string,
      isEstimateEdit: boolean,
      isQuoteEdit: boolean,
      signatureState: any
    ) => {
      if (
        showEstimate !== BuildQuotesStates.IS_ESTIMATE &&
        showEstimate !== BuildQuotesStates.IS_QUOTE
      ) {
        message.error(notificationConstants.SELECT_QUOTE_OR_ESTIMATE);
        return false;
      }

      if (
        (showEstimate === BuildQuotesStates.IS_ESTIMATE && isEstimateEdit) ||
        (showEstimate === BuildQuotesStates.IS_QUOTE && isQuoteEdit)
      ) {
        message.error(notificationConstants.CLICK_EDIT_TO_SAVE);
        return false;
      }

      if (showEstimate === BuildQuotesStates.IS_QUOTE && !signatureState.url) {
        message.error(notificationConstants.UPLOAD_SIGNATURE_OR_NAME);
        return false;
      }

      // Validate required fields for quote
      if (showEstimate === BuildQuotesStates.IS_QUOTE) {
        const requiredFields = [
          {
            field: fieldNames.QUOTE_PRINT_NAME,
            message: notificationConstants.ENTER_QUOTE_PRINT_NAME,
          },
          {
            field: fieldNames.QUOTE_TITLE,
            message: notificationConstants.ENTER_QUOTE_TITLE,
          },
          {
            field: fieldNames.QUOTE_PHONE,
            message: notificationConstants.ENTER_QUOTE_PHONE,
          },
          {
            field: fieldNames.QUOTE_EMAIL,
            message: notificationConstants.ENTER_QUOTE_EMAIL,
          },
          {
            field: fieldNames.QUOTE_DATE,
            message: notificationConstants.ENTER_QUOTE_DATE,
          },
        ];

        for (const { field, message: errorMessage } of requiredFields) {
          if (!getValues(field)) {
            message.error(errorMessage);
            setError(field, {
              type: ErrorTypes.DATE.REQUIRED,
              message: ErrorTypes.DATE.REQUIRED,
            });
            return false;
          }
        }
      }

      return true;
    },
    [getValues, setError]
  );

  const validateQuoteEditFields = useCallback(() => {
    const quoteDate = getValues(fieldNames.QUOTE_DATE);
    const printName = getValues(fieldNames.QUOTE_PRINT_NAME);
    const title = getValues(fieldNames.QUOTE_TITLE);
    const phone = getValues(fieldNames.QUOTE_PHONE);
    const email = getValues(fieldNames.QUOTE_EMAIL);

    if (!printName) {
      message.error(notificationConstants.ENTER_QUOTE_PRINT_NAME);
      setError(fieldNames.QUOTE_PRINT_NAME, {
        type: ErrorTypes.DATE.REQUIRED,
        message: ErrorTypes.DATE.REQUIRED,
      });
      return false;
    }

    if (printName && !alphaNumericRegex.test(printName)) {
      message.error(notificationConstants.PRINT_NAME_ALPHANUMBERIC_FORMAT);
      setError(fieldNames.QUOTE_PRINT_NAME, {
        type: ErrorTypes.EMAIL.INVALID,
        message: notificationConstants.PRINT_NAME_ALPHANUMBERIC_FORMAT,
      });
      return false;
    }

    if (!title) {
      message.error(notificationConstants.ENTER_QUOTE_TITLE);
      setError(fieldNames.QUOTE_TITLE, {
        type: ErrorTypes.DATE.REQUIRED,
        message: ErrorTypes.DATE.REQUIRED,
      });
      return false;
    }

    if (title && !alphabetsOnlyRegexWithSpace.test(title)) {
      message.error(notificationConstants.TITLE_ALPHABET_FORMAT);
      setError(fieldNames.QUOTE_TITLE, {
        type: ErrorTypes.EMAIL.INVALID,
        message: notificationConstants.TITLE_ALPHABET_FORMAT,
      });
      return false;
    }

    if (title && title.length > 50) {
      message.error(notificationConstants.TITLE_MAX_LIMIT);
      setError(fieldNames.QUOTE_TITLE, {
        type: ErrorTypes.EMAIL.LIMIT,
        message: ErrorTypes.DATE.REQUIRED,
      });
      return false;
    }

    if (!phone) {
      message.error(notificationConstants.ENTER_QUOTE_PHONE);
      setError(fieldNames.QUOTE_PHONE, {
        type: ErrorTypes.DATE.REQUIRED,
        message: ErrorTypes.DATE.REQUIRED,
      });
      return false;
    }

    if (phone && !phoneNumberRegex.test(phone)) {
      message.error(notificationConstants.PHONE_NUMBER_FORMAT);
      setError(fieldNames.QUOTE_PHONE, {
        type: ErrorTypes.EMAIL.INVALID,
        message: notificationConstants.PHONE_NUMBER_FORMAT,
      });
      return false;
    }

    if (!email) {
      message.error(notificationConstants.ENTER_QUOTE_EMAIL);
      setError(fieldNames.QUOTE_EMAIL, {
        type: ErrorTypes.DATE.REQUIRED,
        message: ErrorTypes.DATE.REQUIRED,
      });
      return false;
    }

    if (email) {
      if (email.length > 50) {
        message.error(notificationConstants.EMAIL_MAX_LIMIT);
        setError(fieldNames.QUOTE_EMAIL, {
          type: ErrorTypes.EMAIL.LIMIT,
          message: ErrorTypes.DATE.REQUIRED,
        });
        return false;
      }
      if (!emailTestRegex.test(email)) {
        message.error(notificationConstants.INVALID_EMAIL_FORMAT);
        setError(fieldNames.QUOTE_EMAIL, {
          type: ErrorTypes.EMAIL.INVALID,
          message: notificationConstants.INVALID_EMAIL_FORMAT,
        });
        return false;
      }
    }

    if (quoteDate) {
      const date = moment(quoteDate, DateFormat.PARSE, true);
      if (!date.isValid()) {
        message.error(ValidationMessages.DATE.INVALID_FORMAT);
        setError(fieldNames.QUOTE_DATE, {
          type: ErrorTypes.DATE.FORMAT,
          message: ValidationMessages.DATE.INVALID_FORMAT,
        });
        return false;
      }
      if (date.isAfter(moment().endOf("day"))) {
        message.error(ValidationMessages.DATE.FUTURE_DATE);
        setError(fieldNames.QUOTE_DATE, {
          type: ErrorTypes.DATE.FUTURE,
          message: ValidationMessages.DATE.FUTURE_DATE,
        });
        return false;
      }
    }

    return true;
  }, [getValues, setError]);

  return {
    validateDownloadAndSend,
    validateQuoteEditFields,
  };
};
